import { useConnectorFilePickerContext } from '#pplx/frontend/apps/ask/connectors-file-pickers/useConnectorFilePickerContext.js';
import { useConnectorsMap } from '#pplx/frontend/apps/ask/connectors/hooks/useConnectorsMap.js';
import { useEnsureMigrationTargetPickerConnection } from '#pplx/frontend/apps/ask/connectors/hooks/useEnsureMigrationTargetPickerConnection.js';
import { isConnectorFilePickerReady } from '#pplx/frontend/apps/ask/connectors/utils/connectors.js';
import { boxItemToPickerDocument, dropboxItemToPickerDocument, microsoftItemToPickerDocument, } from '#pplx/frontend/components/hooks/toPickerDocument.js';
import { useDropboxPicker } from '#pplx/frontend/components/hooks/useDropboxPicker.js';
import { useGoogleDrivePicker } from '#pplx/frontend/components/hooks/useGoogleDrivePicker.js';
import { createContext, useCallback, useContext, useMemo } from 'react';
export const ConnectorFilePickerOverrideContext = createContext(null);
ConnectorFilePickerOverrideContext.displayName = 'ConnectorFilePickerOverrideContext';
export const useConnectorFilePicker = ({ connectorId, onFilesSelected, onError, googlePromptBehavior, }) => {
    const reason = 'sources-menu-attach-file-connector';
    const pickerOverride = useContext(ConnectorFilePickerOverrideContext);
    const { openPicker: openGoogleDrivePicker, isReady: isGoogleDriveReady } = useGoogleDrive({
        onSelectFiles: onFilesSelected,
        reason,
        googlePromptBehavior,
        onError,
    });
    const { openPicker: openOneDrivePicker, isReady: isOneDriveReady } = useMicrosoft({
        connectorName: 'onedrive',
        onSelectFiles: onFilesSelected,
        reason,
        onError,
    });
    const { openPicker: openSharePointPicker, isReady: isSharePointReady } = useMicrosoft({
        connectorName: 'sharepoint',
        onSelectFiles: onFilesSelected,
        reason,
        onError,
    });
    const { openPicker: openDropboxPicker, isReady: isDropboxReady } = useDropbox({
        onSelectFiles: onFilesSelected,
        reason,
        onError,
    });
    const { openPicker: openBoxPicker, isReady: isBoxReady } = useBox({
        onSelectFiles: onFilesSelected,
        reason,
        onError,
    });
    const isReady = useMemo(() => {
        if (pickerOverride)
            return true;
        switch (connectorId) {
            case 'google_drive':
                return isGoogleDriveReady;
            case 'onedrive':
                return isOneDriveReady;
            case 'sharepoint':
                return isSharePointReady;
            case 'dropbox':
                return isDropboxReady;
            case 'box':
                return isBoxReady;
            default:
                return false;
        }
    }, [
        connectorId,
        pickerOverride,
        isGoogleDriveReady,
        isOneDriveReady,
        isSharePointReady,
        isDropboxReady,
        isBoxReady,
    ]);
    const openFilePicker = useCallback(() => {
        if (pickerOverride) {
            pickerOverride({ connectorId, onFilesSelected, onError });
            return;
        }
        switch (connectorId) {
            case 'google_drive':
                void Promise.resolve(openGoogleDrivePicker()).catch(() => undefined);
                break;
            case 'onedrive':
                openOneDrivePicker();
                break;
            case 'sharepoint':
                openSharePointPicker();
                break;
            case 'dropbox':
                openDropboxPicker();
                break;
            case 'box':
                openBoxPicker();
                break;
            default:
                throw new Error(`Connector ${connectorId} not implemented`);
        }
    }, [
        connectorId,
        onError,
        onFilesSelected,
        openGoogleDrivePicker,
        openOneDrivePicker,
        openSharePointPicker,
        openDropboxPicker,
        openBoxPicker,
        pickerOverride,
    ]);
    return useMemo(() => ({
        openFilePicker,
        isReady,
    }), [openFilePicker, isReady]);
};
const useGoogleDrive = ({ onSelectFiles, reason, googlePromptBehavior, onError }) => {
    const { connectorsMap } = useConnectorsMap({
        reason,
        skipConnectorPickerCredentials: false,
    });
    const googleDrive = connectorsMap?.google_drive;
    const isReady = isConnectorFilePickerReady(googleDrive);
    const ensureMigrationTargetPickerConnection = useEnsureMigrationTargetPickerConnection({
        reason,
    });
    const { openPicker: openGoogleDrivePicker } = useGoogleDrivePicker({
        isAudioVideoFilesEnabled: true,
        onError,
    });
    const openPicker = useCallback(async () => {
        const { connector, shouldOpenPicker } = await ensureMigrationTargetPickerConnection({
            currentConnector: googleDrive,
            targetSourceId: 'google_drive',
        });
        if (!shouldOpenPicker)
            return;
        const pickerGoogleDrive = connector ?? googleDrive;
        openGoogleDrivePicker({
            apiKey: pickerGoogleDrive?.picker_credentials?.api_key ?? '',
            clientId: pickerGoogleDrive?.picker_credentials?.client_id ?? '',
            loginHint: pickerGoogleDrive?.connection_display_name ?? '',
            mode: 'attachment',
            promptBehavior: googlePromptBehavior,
            scopes: pickerGoogleDrive?.picker_credentials?.scopes,
            onPicked: (files) => {
                onSelectFiles({
                    type: 'google_drive',
                    files,
                });
            },
        });
    }, [
        googleDrive,
        ensureMigrationTargetPickerConnection,
        openGoogleDrivePicker,
        onSelectFiles,
        googlePromptBehavior,
    ]);
    return useMemo(() => ({
        openPicker,
        isReady,
    }), [openPicker, isReady]);
};
const useMicrosoft = ({ connectorName, onSelectFiles, reason, onError }) => {
    const { connectorsMap } = useConnectorsMap({
        reason,
        skipConnectorPickerCredentials: false,
    });
    const connectorData = connectorsMap?.[connectorName];
    const isReady = isConnectorFilePickerReady(connectorData);
    const { openMicrosoftPicker, closeMicrosoftPicker } = useConnectorFilePickerContext();
    const openPicker = useCallback(() => {
        const options = {
            connector: connectorName,
            mode: 'attachment',
            clientId: connectorData?.picker_credentials?.client_id ?? '',
            tenantName: connectorData?.picker_credentials?.tenant_name ?? '',
            loginHint: connectorData?.connection_display_name ?? '',
            accountIdentifier: connectorData?.picker_credentials?.account_identifier,
            connectionUuid: connectorData?.connection_uuid,
            isAudioVideoFilesEnabled: true,
            onPicked: (items) => {
                const files = items
                    .map((item) => microsoftItemToPickerDocument(item, connectorName))
                    .filter((file) => file !== null);
                onSelectFiles({
                    type: connectorName,
                    files,
                });
                closeMicrosoftPicker();
            },
            onError: (_error) => {
                onError?.();
                closeMicrosoftPicker();
            },
            onCancel: () => {
                closeMicrosoftPicker();
            },
        };
        openMicrosoftPicker(options);
    }, [
        connectorName,
        connectorData,
        onSelectFiles,
        openMicrosoftPicker,
        closeMicrosoftPicker,
        onError,
    ]);
    return useMemo(() => ({
        openPicker,
        isReady,
    }), [openPicker, isReady]);
};
const useDropbox = ({ onSelectFiles, reason, onError }) => {
    const { connectorsMap } = useConnectorsMap({
        reason,
        skipConnectorPickerCredentials: false,
    });
    const dropbox = connectorsMap?.dropbox;
    const isReady = isConnectorFilePickerReady(dropbox);
    const { openPicker: openDropboxPicker } = useDropboxPicker();
    const openPicker = useCallback(() => {
        void openDropboxPicker({
            clientId: dropbox?.picker_credentials?.client_id ?? '',
            mode: 'attachment',
            isAudioVideoFilesEnabled: true,
            onPicked: (items) => {
                const files = items
                    .map((item) => dropboxItemToPickerDocument(item))
                    .filter((file) => file !== null);
                onSelectFiles({
                    type: 'dropbox',
                    files,
                });
            },
            onError: () => onError?.(),
        }).catch(() => onError?.());
    }, [dropbox, openDropboxPicker, onSelectFiles, onError]);
    return useMemo(() => ({
        openPicker,
        isReady,
    }), [openPicker, isReady]);
};
const useBox = ({ onSelectFiles, reason, onError }) => {
    const { connectorsMap } = useConnectorsMap({
        reason,
        skipConnectorPickerCredentials: false,
    });
    const box = connectorsMap?.box;
    const isReady = isConnectorFilePickerReady(box);
    const { openBoxPicker } = useConnectorFilePickerContext();
    const openPicker = useCallback(() => {
        openBoxPicker({
            apiKey: box?.picker_credentials?.api_key ?? '',
            onPicked: (items) => {
                const files = items.map((item) => boxItemToPickerDocument(item));
                onSelectFiles({
                    type: 'box',
                    files,
                });
            },
            onError,
        });
    }, [box, openBoxPicker, onSelectFiles, onError]);
    return useMemo(() => ({
        openPicker,
        isReady,
    }), [openPicker, isReady]);
};
//# sourceMappingURL=useConnectorFilePicker.js.map